import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, BookOpen, LogOut, User, Settings } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { client } from '../api/client';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, reset } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await client.auth.signOut();
    reset();
    navigate('/');
  };

  const navItems = [
    { label: '学习中心', path: '/learning' },
    { label: '进度', path: '/progress' },
    { label: 'AI推荐', path: '/ai-recommend' },
    { label: '排行榜', path: '/leaderboard' },
    { label: 'VCE数学', path: '/vce/mathematics' },
    { label: '题库', path: '/subjects' },
    { label: '教学材料', path: '/materials' },
    { label: 'AI生成', path: '/generator' },
    { label: '错题本', path: '/mistakes' },
  ];

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              AusLearn
            </span>
          </Link>

          {/* Desktop Navigation */}
          {user && (
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* User Menu */}
                <div className="hidden md:flex items-center gap-3">
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                    </div>
                    <span className="max-w-[100px] truncate">{user.name || user.email}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="退出登录"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium rounded-full hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-105"
              >
                登录 / 注册
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && user && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              <hr className="my-2 border-slate-100" />
              <Link
                to="/dashboard"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <User className="w-5 h-5" />
                个人中心
              </Link>
              <Link
                to="/settings"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <Settings className="w-5 h-5" />
                设置
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg text-left"
              >
                <LogOut className="w-5 h-5" />
                退出登录
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
